/**
 * Anzeigeformate.
 *
 * Alles deutsch: Datum als TT.MM.JJJJ, Dezimalkomma, Tausenderpunkt. Die
 * Datumsfelder der Formulare arbeiten dagegen mit dem ISO-Format
 * (JJJJ-MM-TT) — das erwartet <input type="date">, und die API erwartet es
 * ebenfalls.
 */

/** ISO-Datum → deutsche Anzeige. */
export function datum(wert: string | null | undefined): string {
  if (!wert) return '—';
  // Aus der Datenbank kommt bei `date` ein reines "2026-08-17", bei
  // `timestamptz` ein vollständiger Zeitstempel. Beides beginnt mit dem
  // Datum; der Zeitanteil wird hier nicht gebraucht.
  const teile = wert.slice(0, 10).split('-');
  if (teile.length !== 3) return wert;
  return `${teile[2]}.${teile[1]}.${teile[0]}`;
}

/** Zeitstempel → "17.08.2026, 14:32". */
export function datumZeit(wert: string | null | undefined): string {
  if (!wert) return '—';
  const zeitpunkt = new Date(wert);
  if (Number.isNaN(zeitpunkt.getTime())) return wert;
  return zeitpunkt.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Heute als ISO-Datum — Vorbelegung für Formulare. */
export function heute(): string {
  const jetzt = new Date();
  // Nicht toISOString(): Das rechnet nach UTC um und liefert vor 2 Uhr
  // morgens deutscher Zeit noch den Vortag.
  const monat = String(jetzt.getMonth() + 1).padStart(2, '0');
  const tag = String(jetzt.getDate()).padStart(2, '0');
  return `${jetzt.getFullYear()}-${monat}-${tag}`;
}

export function jahrHeute(): number {
  return new Date().getFullYear();
}

/** Zahl mit deutschem Dezimalkomma. */
export function zahl(wert: string | number | null | undefined, stellen = 2): string {
  if (wert === null || wert === undefined || wert === '') return '—';
  const n = typeof wert === 'number' ? wert : Number.parseFloat(wert);
  if (Number.isNaN(n)) return String(wert);
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: stellen,
    maximumFractionDigits: stellen,
  }).format(n);
}

/** Prozentwert — ganze Prozente ohne Nachkommastellen. */
export function prozent(wert: string | number | null | undefined): string {
  if (wert === null || wert === undefined || wert === '') return '—';
  const n = typeof wert === 'number' ? wert : Number.parseFloat(wert);
  if (Number.isNaN(n)) return String(wert);
  const stellen = Number.isInteger(n) ? 0 : 2;
  return `${zahl(n, stellen)} %`;
}

/** Dateigröße für Menschen. */
export function groesse(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${zahl(bytes / 1024, 0)} kB`;
  return `${zahl(bytes / (1024 * 1024), 1)} MB`;
}

/**
 * Status als Wort für die Anzeige.
 *
 * Die Datenbank speichert Kleinbuchstaben ohne Umlaute; angezeigt wird, was
 * ein Mensch liest.
 */
const STATUS_WORTE: Record<string, string> = {
  entwurf: 'Entwurf',
  erfasst: 'Erfasst',
  gebucht: 'Gebucht',
  storniert: 'Storniert',
  festgeschrieben: 'Festgeschrieben',
  gestellt: 'Gestellt',
  teilbezahlt: 'Teilbezahlt',
  bezahlt: 'Bezahlt',
  ueberfaellig: 'Überfällig',
  versendet: 'Versendet',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
  abgelaufen: 'Abgelaufen',
  offen: 'Offen',
  zugeordnet: 'Zugeordnet',
  teilzugeordnet: 'Teilweise zugeordnet',
  privat: 'Privat',
  unerheblich: 'Unerheblich',
  ignoriert: 'Ignoriert',
  // Zustimmung zum Kontoabruf. „angefragt" heißt: angelegt, aber vom
  // Kontoinhaber im Online-Banking noch nicht bestätigt.
  angefragt: 'Bestätigung offen',
  gueltig: 'Gültig',
  widerrufen: 'Widerrufen',
};

export function statusWort(status: string | null | undefined): string {
  if (!status) return '—';
  return STATUS_WORTE[status] ?? status;
}

const MONATE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export function monatsname(monat: number): string {
  return MONATE[monat - 1] ?? String(monat);
}
